import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { cn, formatBytes, formatUptime, timeAgo, formatDate, getStatusColor, formatNumber } from '@/lib/utils';
import { Card, CardHeader, CardTitle, CardBody } from '@/components/ui/Card';
import { Badge, StatusDot } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Tabs, TabContent } from '@/components/ui/Tabs';
import { Drawer } from '@/components/ui/Drawer';
import { Modal, ConfirmDialog } from '@/components/ui/Modal';
import { ProgressBar } from '@/components/charts';
import { EmptyState, LoadingState } from '@/components/ui/States';
import { toast } from '@/components/ui/Toast';
import { Dropdown, DropdownItem, DropdownSeparator } from '@/components/ui/Dropdown';
import { PageContainer } from '@/components/layout/PageHeader';
import { useDevice } from '@/hooks';
import { apiService } from '@/api';
import {
  ArrowLeft,
  Monitor,
  Cpu,
  MemoryStick,
  HardDrive,
  Network,
  Shield,
  Activity,
  FolderOpen,
  Terminal,
  TerminalSquare,
  RotateCw,
  Power,
  Lock,
  LogOut,
  RefreshCw,
  FileCode2,
  Download,
  MoreHorizontal,
  Server,
  Clock,
  User,
  Wifi,
  Eye,
  Search,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Play,
  Settings,
  Sliders,
  Tag,
  Plus,
  X,
  FileText,
  Layers,
  Wrench,
  Key,
  ShieldCheck,
  Zap,
  Trash2,
} from 'lucide-react';

export function DeviceDetail() {
  const { id } = useParams();
  const { data: device, isLoading } = useDevice(id);
  const navigate = useNavigate();

  // Active Tab state
  const [activeTab, setActiveTab] = useState('summary');

  // Confirmation dialogs for destructive actions
  const [confirmAction, setConfirmAction] = useState<'restart' | 'shutdown' | 'delete' | null>(null);

  // Tab specific local state filters
  const [softwareSearch, setSoftwareSearch] = useState('');
  const [serviceFilter, setServiceFilter] = useState('');
  const [processSortKey, setProcessSortKey] = useState<'cpu' | 'memory' | 'name' | 'pid'>('cpu');
  const [processSortOrder, setProcessSortOrder] = useState<'asc' | 'desc'>('desc');
  const [eventLogLevel, setEventLogLevel] = useState<string>('all');
  const [patchFilter, setPatchFilter] = useState<string>('all');

  // Script Modal state
  const [runScriptModalOpen, setRunScriptModalOpen] = useState(false);
  const [selectedScript, setSelectedScript] = useState('System Health Diagnostics');

  // Settings tab local state
  const [heartbeatInterval, setHeartbeatInterval] = useState('60');
  const [newTagInput, setNewTagInput] = useState('');
  const [deviceTags, setDeviceTags] = useState<string[]>([]);
  const [maintenanceMode, setMaintenanceMode] = useState(false);

  useEffect(() => {
    if (device?.tags && device.tags.length > 0) {
      setDeviceTags(device.tags);
    }
  }, [device]);

  if (isLoading) {
    return (
      <PageContainer>
        <div className="p-6">
          <LoadingState rows={8} />
        </div>
      </PageContainer>
    );
  }

  if (!device) {
    return (
      <PageContainer>
        <div className="p-6">
          <EmptyState
            icon={<Server className="h-10 w-10 text-text-muted" />}
            title="Device Not Found"
            description={`Could not find device with ID "${id}". It may have been decommissioned or deleted.`}
            action={
              <Button variant="secondary" onClick={() => navigate('/devices')}>
                <ArrowLeft className="h-4 w-4 mr-1.5" />
                Back to Devices
              </Button>
            }
          />
        </div>
      </PageContainer>
    );
  }

  const handleAction = async (actionName: string) => {
    try {
      const api = apiService as any;
      if (typeof api.deviceAction === 'function') {
        await api.deviceAction(device.id, actionName);
      }
    } catch {
      toast(`Failed to send ${actionName} to ${device.hostname}`, 'error');
      return;
    }
    switch (actionName) {
      case 'lock':
        toast(`Lock command sent to ${device.hostname}`);
        break;
      case 'sign_out':
        toast(`Sign out command sent for active user on ${device.hostname}`);
        break;
      case 'restart_agent':
        toast(`Agent restart initiated on ${device.hostname}`);
        break;
      case 'refresh_inventory':
        toast(`Inventory refresh queued for ${device.hostname}`);
        break;
      default:
        toast(`Command ${actionName} sent to ${device.hostname}`);
        break;
    }
  };

  const handleServiceAction = (serviceName: string, action: 'start' | 'stop' | 'restart') => {
    if (action === 'start') {
      toast(`Service "${serviceName}" started on ${device.hostname}`);
    } else if (action === 'stop') {
      toast(`Service "${serviceName}" stopped on ${device.hostname}`, 'warning');
    } else {
      toast(`Service "${serviceName}" restarted on ${device.hostname}`);
    }
  };

  const handleSaveSettings = () => {
    toast(`Settings updated for ${device.hostname}`);
  };

  const handleAddTag = () => {
    if (newTagInput.trim() && !deviceTags.includes(newTagInput.trim())) {
      setDeviceTags([...deviceTags, newTagInput.trim()]);
      setNewTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setDeviceTags(deviceTags.filter((t) => t !== tagToRemove));
  };

  // Sort processes
  const sortedProcesses = [...(device.processes || [])].sort((a, b) => {
    let mul = processSortOrder === 'asc' ? 1 : -1;
    if (processSortKey === 'cpu') return (a.cpu - b.cpu) * mul;
    if (processSortKey === 'memory') return (a.memoryMb - b.memoryMb) * mul;
    if (processSortKey === 'name') return a.name.localeCompare(b.name) * mul;
    if (processSortKey === 'pid') return (a.pid - b.pid) * mul;
    return 0;
  });

  // Filter software
  const filteredSoftware = (device.installedSoftware || []).filter((sw: any) => {
    const q = softwareSearch.toLowerCase();
    return (
      (sw.name || '').toLowerCase().includes(q) ||
      (sw.publisher || '').toLowerCase().includes(q) ||
      (sw.category || '').toLowerCase().includes(q)
    );
  });

  const filteredServices = (device.services || []).filter((s: any) => {
    const q = serviceFilter.toLowerCase();
    return (
      (s.name || '').toLowerCase().includes(q) ||
      (s.displayName || '').toLowerCase().includes(q) ||
      (s.status || '').toLowerCase().includes(q)
    );
  });

  // Filter event logs
  const filteredEventLogs = (device.eventLogs || []).filter((log: any) => {
    if (eventLogLevel === 'all') return true;
    return log.level === eventLogLevel;
  });

  // Filter patches
  const filteredPatches = (device.patches || []).filter((patch: any) => {
    if (patchFilter === 'all') return true;
    return patch.status === patchFilter;
  });

  const tabList = [
    { id: 'summary', label: 'Summary' },
    { id: 'hardware', label: 'Hardware' },
    { id: 'os', label: 'OS' },
    { id: 'network', label: 'Network' },
    { id: 'storage', label: 'Storage' },
    { id: 'software', label: 'Software', count: device.installedSoftware?.length },
    { id: 'services', label: 'Services', count: device.services?.length },
    { id: 'processes', label: 'Processes', count: device.processes?.length },
    { id: 'event_logs', label: 'Event Logs', count: device.eventLogs?.length },
    { id: 'patches', label: 'Patches', count: device.patches?.length },
    { id: 'security', label: 'Security' },
    { id: 'automation', label: 'Automation' },
    { id: 'scripts', label: 'Scripts' },
    { id: 'activity', label: 'Activity' },
    { id: 'remote_tools', label: 'Remote Tools' },
    { id: 'settings', label: 'Settings' },
  ];

  return (
    <PageContainer>
      {/* ── Device Header ── */}
      <div className="border-b border-border bg-base-panel/40 px-6 pt-4 pb-4">
        <div className="flex items-center justify-between gap-4 mb-3">
          <Link
            to="/devices"
            className="inline-flex items-center gap-1.5 text-xs text-text-muted hover:text-text-primary transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to Devices
          </Link>

          {/* Action Buttons & Dropdown */}
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate(`/devices/${device.id}/remote-desktop`)}
            >
              <Monitor className="h-3.5 w-3.5 mr-1" />
              Remote Desktop
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate(`/devices/${device.id}/powershell`)}
            >
              <Terminal className="h-3.5 w-3.5 mr-1" />
              PowerShell
            </Button>
            <Dropdown
              trigger={
                <Button variant="secondary" size="sm">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              }
            >
              <DropdownItem
                icon={<Monitor className="h-3.5 w-3.5" />}
                onClick={() => navigate(`/devices/${device.id}/remote-desktop`)}
              >
                Remote Desktop
              </DropdownItem>
              <DropdownItem
                icon={<FolderOpen className="h-3.5 w-3.5" />}
                onClick={() => navigate(`/devices/${device.id}/files`)}
              >
                File Explorer
              </DropdownItem>
              <DropdownItem
                icon={<Terminal className="h-3.5 w-3.5" />}
                onClick={() => navigate(`/devices/${device.id}/powershell`)}
              >
                PowerShell
              </DropdownItem>
              <DropdownItem
                icon={<TerminalSquare className="h-3.5 w-3.5" />}
                onClick={() => navigate(`/devices/${device.id}/cmd`)}
              >
                CMD Prompt
              </DropdownItem>
              <DropdownSeparator />
              <DropdownItem
                icon={<Lock className="h-3.5 w-3.5" />}
                onClick={() => handleAction('lock')}
              >
                Lock Session
              </DropdownItem>
              <DropdownItem
                icon={<LogOut className="h-3.5 w-3.5" />}
                onClick={() => handleAction('sign_out')}
              >
                Sign Out User
              </DropdownItem>
              <DropdownItem
                icon={<RotateCw className="h-3.5 w-3.5" />}
                onClick={() => handleAction('restart_agent')}
              >
                Restart Agent
              </DropdownItem>
              <DropdownItem
                icon={<RefreshCw className="h-3.5 w-3.5" />}
                onClick={() => handleAction('refresh_inventory')}
              >
                Refresh Inventory
              </DropdownItem>
              <DropdownSeparator />
              <DropdownItem
                icon={<RotateCw className="h-3.5 w-3.5" />}
                onClick={() => setConfirmAction('restart')}
              >
                Restart Device
              </DropdownItem>
              <DropdownItem
                icon={<Power className="h-3.5 w-3.5" />}
                danger
                onClick={() => setConfirmAction('shutdown')}
              >
                Shut Down
              </DropdownItem>
              {device.status === 'offline' && (
                <DropdownItem
                  icon={<Trash2 className="h-3.5 w-3.5" />}
                  danger
                  onClick={() => setConfirmAction('delete')}
                >
                  Delete Offline Device
                </DropdownItem>
              )}
            </Dropdown>
          </div>
        </div>

        {/* Hostname & Metadata */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-lg bg-base-elevated border border-border flex items-center justify-center text-accent">
              <Server className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-2.5">
                <h1 className="text-xl font-bold tracking-tight text-text-primary">{device.hostname}</h1>
                <Badge variant={device.status} dot>
                  {String(device.status || 'offline').toUpperCase()}
                </Badge>
                {device.pendingReboot && (
                  <Badge variant="warning">REBOOT PENDING</Badge>
                )}
              </div>
              <div className="flex items-center gap-3 text-xs text-text-muted mt-1 flex-wrap">
                <span className="flex items-center gap-1 text-text-secondary">
                  <User className="h-3 w-3 text-text-muted" />
                  {device.loggedInUser || 'No user logged in'}
                </span>
                <span>•</span>
                <span>Client: <strong className="text-text-secondary">{device.client}</strong></span>
                <span>•</span>
                <span>Site: <strong className="text-text-secondary">{device.site}</strong></span>
                <span>•</span>
                <span>OS: <strong className="text-text-secondary">{device.os} ({device.osVersion})</strong></span>
                <span>•</span>
                <span>Agent: <strong className="text-text-secondary">v{device.agentVersion}</strong></span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Navigation Tabs ── */}
      <div className="px-6 pt-2 bg-base-panel/20">
        <Tabs
          tabs={tabList}
          defaultActive={activeTab}
          onChange={(id) => setActiveTab(id)}
        />
      </div>

      {/* ── Tab Contents ── */}
      <div className="px-6 pt-6">

        {/* SUMMARY TAB */}
        <TabContent active={activeTab} tabId="summary">
          <div className="space-y-6">
            {/* Top Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardBody className="space-y-2">
                  <div className="flex items-center justify-between text-xs text-text-muted font-medium">
                    <span>SYSTEM STATUS</span>
                    <Clock className="h-3.5 w-3.5 text-text-muted" />
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusDot status={device.status} />
                    <span className="text-lg font-semibold capitalize text-text-primary">{device.status}</span>
                  </div>
                  <div className="text-2xs text-text-muted pt-1">
                    Last heartbeat: <span className="text-text-secondary">{timeAgo(device.lastSeen)}</span>
                  </div>
                  <div className="text-2xs text-text-muted">
                    Uptime: <span className="text-text-secondary">{formatUptime(device.uptimeSeconds || 0)}</span>
                  </div>
                </CardBody>
              </Card>

              <Card>
                <CardBody className="space-y-2">
                  <div className="flex items-center justify-between text-xs text-text-muted font-medium">
                    <span>CPU LOAD</span>
                    <Cpu className="h-3.5 w-3.5 text-text-muted" />
                  </div>
                  <div className="text-2xl font-bold text-text-primary tabular">{device.cpu}%</div>
                  <ProgressBar value={device.cpu} showLabel={false} />
                  <div className="text-2xs text-text-muted truncate">
                    {device.cpuModel} ({device.cpuCores} Cores)
                  </div>
                </CardBody>
              </Card>

              <Card>
                <CardBody className="space-y-2">
                  <div className="flex items-center justify-between text-xs text-text-muted font-medium">
                    <span>MEMORY USAGE</span>
                    <MemoryStick className="h-3.5 w-3.5 text-text-muted" />
                  </div>
                  <div className="text-2xl font-bold text-text-primary tabular">{device.memory}%</div>
                  <ProgressBar value={device.memory} showLabel={false} />
                  <div className="text-2xs text-text-muted">
                    {device.memoryUsed?.toFixed(1)} GB used of {device.memoryTotal} GB
                  </div>
                </CardBody>
              </Card>

              <Card>
                <CardBody className="space-y-2">
                  <div className="flex items-center justify-between text-xs text-text-muted font-medium">
                    <span>PRIMARY DISK</span>
                    <HardDrive className="h-3.5 w-3.5 text-text-muted" />
                  </div>
                  <div className="text-2xl font-bold text-text-primary tabular">{device.disk}%</div>
                  <ProgressBar value={device.disk} showLabel={false} />
                  <div className="text-2xs text-text-muted">
                    {device.disks?.[0] ? `${device.disks[0].usedGb} GB / ${device.disks[0].totalGb} GB` : 'Storage Info'}
                  </div>
                </CardBody>
              </Card>
            </div>

            {/* Network & Specs Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Wifi className="h-4 w-4 text-accent" />
                    Network & Connectivity
                  </CardTitle>
                </CardHeader>
                <CardBody className="space-y-3">
                  <div className="grid grid-cols-2 gap-4 text-xs">
                    <div className="p-3 bg-base-elevated rounded border border-border">
                      <span className="text-text-muted block text-2xs uppercase">Public IP Address</span>
                      <span className="font-mono text-text-primary text-sm font-semibold mt-0.5 block">{device.publicIp}</span>
                    </div>
                    <div className="p-3 bg-base-elevated rounded border border-border">
                      <span className="text-text-muted block text-2xs uppercase">Local IP Address</span>
                      <span className="font-mono text-text-primary text-sm font-semibold mt-0.5 block">{device.localIp}</span>
                    </div>
                  </div>
                  <div className="text-xs space-y-1.5 pt-2">
                    <div className="flex justify-between py-1 border-b border-border">
                      <span className="text-text-muted">Client & Site</span>
                      <span className="text-text-primary font-medium">{device.client} / {device.site}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-border">
                      <span className="text-text-muted">Active User</span>
                      <span className="text-text-primary font-medium">{device.loggedInUser || 'None'}</span>
                    </div>
                    <div className="flex justify-between py-1">
                      <span className="text-text-muted">Agent Version</span>
                      <span className="text-text-primary font-medium">v{device.agentVersion}</span>
                    </div>
                  </div>
                </CardBody>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Server className="h-4 w-4 text-accent" />
                    Hardware Specs
                  </CardTitle>
                </CardHeader>
                <CardBody className="text-xs space-y-2">
                  <div className="flex justify-between py-1.5 border-b border-border">
                    <span className="text-text-muted">Manufacturer</span>
                    <span className="text-text-primary font-medium">{device.manufacturer}</span>
                  </div>
                  <div className="flex justify-between py-1.5 border-b border-border">
                    <span className="text-text-muted">Model</span>
                    <span className="text-text-primary font-medium">{device.model}</span>
                  </div>
                  <div className="flex justify-between py-1.5 border-b border-border">
                    <span className="text-text-muted">Serial Number</span>
                    <span className="text-text-primary font-mono">{device.serialNumber}</span>
                  </div>
                  <div className="flex justify-between py-1.5 border-b border-border">
                    <span className="text-text-muted">Processor</span>
                    <span className="text-text-primary font-medium truncate max-w-[240px]">{device.cpuModel}</span>
                  </div>
                  <div className="flex justify-between py-1.5">
                    <span className="text-text-muted">Total RAM</span>
                    <span className="text-text-primary font-medium">{device.memoryTotal} GB</span>
                  </div>
                </CardBody>
              </Card>
            </div>

            {/* Mini Lists: Recent Alerts, Jobs, Sessions */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-status-warning" />
                    Recent Alerts
                  </CardTitle>
                </CardHeader>
                <CardBody className="p-0">
                  {device.recentAlerts && device.recentAlerts.length > 0 ? (
                    <div className="divide-y divide-border">
                      {device.recentAlerts.slice(0, 4).map((alert: any) => (
                        <div key={alert.id} className="p-3 hover:bg-base-hover/50 text-xs">
                          <div className="flex items-center justify-between mb-1">
                            <Badge variant={alert.severity}>{alert.severity}</Badge>
                            <span className="text-2xs text-text-muted">{timeAgo(alert.createdAt)}</span>
                          </div>
                          <p className="text-text-primary font-medium line-clamp-1">{alert.ruleName}</p>
                          <p className="text-text-muted text-2xs line-clamp-1 mt-0.5">{alert.message}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-6 text-center text-xs text-text-muted">No active alerts reported</div>
                  )}
                </CardBody>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="h-4 w-4 text-accent" />
                    Recent Jobs
                  </CardTitle>
                </CardHeader>
                <CardBody className="p-0">
                  {device.recentJobs && device.recentJobs.length > 0 ? (
                    <div className="divide-y divide-border">
                      {device.recentJobs.slice(0, 4).map((job: any) => (
                        <div key={job.id} className="p-3 hover:bg-base-hover/50 text-xs">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-text-primary font-medium">{job.name}</span>
                            <Badge variant={job.status}>{job.status}</Badge>
                          </div>
                          <div className="flex items-center justify-between text-2xs text-text-muted mt-1">
                            <span>Initiated by: {job.initiatedBy}</span>
                            <span>{timeAgo(job.createdAt)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-6 text-center text-xs text-text-muted">No recent jobs executed</div>
                  )}
                </CardBody>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Monitor className="h-4 w-4 text-text-secondary" />
                    Recent Remote Sessions
                  </CardTitle>
                </CardHeader>
                <CardBody className="p-0">
                  {device.recentSessions && device.recentSessions.length > 0 ? (
                    <div className="divide-y divide-border">
                      {device.recentSessions.slice(0, 4).map((session: any) => (
                        <div key={session.id} className="p-3 hover:bg-base-hover/50 text-xs">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-text-primary font-medium">{session.technicianName}</span>
                            <Badge variant={session.status}>{session.status}</Badge>
                          </div>
                          <div className="flex items-center justify-between text-2xs text-text-muted mt-0.5">
                            <span className="capitalize">{session.mode.replace('_', ' ')}</span>
                            <span>{timeAgo(session.startedAt)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-6 text-center text-xs text-text-muted">No remote sessions recorded</div>
                  )}
                </CardBody>
              </Card>
            </div>
          </div>
        </TabContent>

        {/* HARDWARE TAB */}
        <TabContent active={activeTab} tabId="hardware">
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Cpu className="h-4 w-4 text-accent" />
                    Processor (CPU)
                  </CardTitle>
                </CardHeader>
                <CardBody className="space-y-4">
                  <div>
                    <span className="text-xs text-text-muted block uppercase">CPU Model</span>
                    <span className="text-sm font-semibold text-text-primary">{device.cpuModel}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-xs">
                    <div className="p-3 bg-base-elevated rounded border border-border">
                      <span className="text-text-muted block text-2xs uppercase">Cores / Threads</span>
                      <span className="text-lg font-bold text-text-primary">{device.cpuCores}</span>
                    </div>
                    <div className="p-3 bg-base-elevated rounded border border-border">
                      <span className="text-text-muted block text-2xs uppercase">Current Utilization</span>
                      <span className="text-lg font-bold text-text-primary tabular">{device.cpu}%</span>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <span className="text-xs text-text-muted">Utilization Load</span>
                    <ProgressBar value={device.cpu} showLabel />
                  </div>
                </CardBody>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MemoryStick className="h-4 w-4 text-accent" />
                    System Memory (RAM)
                  </CardTitle>
                </CardHeader>
                <CardBody className="space-y-4">
                  <div className="grid grid-cols-3 gap-3 text-xs">
                    <div className="p-3 bg-base-elevated rounded border border-border">
                      <span className="text-text-muted block text-2xs uppercase">Total RAM</span>
                      <span className="text-base font-bold text-text-primary">{device.memoryTotal} GB</span>
                    </div>
                    <div className="p-3 bg-base-elevated rounded border border-border">
                      <span className="text-text-muted block text-2xs uppercase">Used RAM</span>
                      <span className="text-base font-bold text-text-primary">{device.memoryUsed?.toFixed(1)} GB</span>
                    </div>
                    <div className="p-3 bg-base-elevated rounded border border-border">
                      <span className="text-text-muted block text-2xs uppercase">Free RAM</span>
                      <span className="text-base font-bold text-text-primary">
                        {(device.memoryTotal - (device.memoryUsed || 0)).toFixed(1)} GB
                      </span>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <span className="text-xs text-text-muted">Memory Load</span>
                    <ProgressBar value={device.memory} showLabel />
                  </div>
                </CardBody>
              </Card>
            </div>

            {/* Storage Disks Table */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <HardDrive className="h-4 w-4 text-accent" />
                  Storage Disks & Volumes
                </CardTitle>
              </CardHeader>
              <CardBody className="p-0 overflow-x-auto">
                <table className="w-full text-xs text-left">
                  <thead className="bg-base-panel border-b border-border text-text-muted font-medium uppercase text-2xs">
                    <tr>
                      <th className="px-4 py-3">Drive</th>
                      <th className="px-4 py-3">Label</th>
                      <th className="px-4 py-3">Type</th>
                      <th className="px-4 py-3">Total Capacity</th>
                      <th className="px-4 py-3">Used Space</th>
                      <th className="px-4 py-3">Free Space</th>
                      <th className="px-4 py-3 w-48">Usage Bar</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {device.disks && device.disks.length > 0 ? (
                      device.disks.map((disk: any) => {
                        const freeGb = Math.max(0, disk.totalGb - disk.usedGb);
                        const usagePct = Math.round((disk.usedGb / disk.totalGb) * 100);
                        return (
                          <tr key={disk.drive} className="hover:bg-base-hover/50">
                            <td className="px-4 py-3 font-semibold text-text-primary">{disk.drive}</td>
                            <td className="px-4 py-3 text-text-secondary">{disk.label || 'Local Disk'}</td>
                            <td className="px-4 py-3">
                              <Badge variant="default" className="uppercase">
                                {disk.type}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 tabular text-text-primary font-medium">{disk.totalGb} GB</td>
                            <td className="px-4 py-3 tabular text-text-secondary">{disk.usedGb} GB</td>
                            <td className="px-4 py-3 tabular text-text-muted">{freeGb} GB</td>
                            <td className="px-4 py-3">
                              <ProgressBar value={usagePct} showLabel />
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={7} className="px-4 py-6 text-center text-text-muted">
                          No disk information available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </CardBody>
            </Card>
          </div>
        </TabContent>

        {/* OS TAB */}
        <TabContent active={activeTab} tabId="os">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileCode2 className="h-4 w-4 text-accent" />
                Operating System Details
              </CardTitle>
            </CardHeader>
            <CardBody className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-xs">
                <div className="p-3 bg-base-elevated rounded border border-border space-y-1">
                  <span className="text-text-muted text-2xs uppercase block">OS Name</span>
                  <span className="text-text-primary font-semibold text-sm block">{device.os}</span>
                </div>
                <div className="p-3 bg-base-elevated rounded border border-border space-y-1">
                  <span className="text-text-muted text-2xs uppercase block">OS Version / Build</span>
                  <span className="text-text-primary font-semibold text-sm block">{device.osVersion}</span>
                </div>
                <div className="p-3 bg-base-elevated rounded border border-border space-y-1">
                  <span className="text-text-muted text-2xs uppercase block">Architecture</span>
                  <span className="text-text-primary font-semibold text-sm block">64-bit (x64)</span>
                </div>
                <div className="p-3 bg-base-elevated rounded border border-border space-y-1">
                  <span className="text-text-muted text-2xs uppercase block">Install / Enrollment Date</span>
                  <span className="text-text-primary font-medium block">{formatDate(device.enrolledAt)}</span>
                </div>
                <div className="p-3 bg-base-elevated rounded border border-border space-y-1">
                  <span className="text-text-muted text-2xs uppercase block">Last Boot Time</span>
                  <span className="text-text-primary font-medium block">{formatDate(device.lastBoot)}</span>
                </div>
                <div className="p-3 bg-base-elevated rounded border border-border space-y-1">
                  <span className="text-text-muted text-2xs uppercase block">System Uptime</span>
                  <span className="text-text-primary font-medium block">{formatUptime(device.uptimeSeconds)}</span>
                </div>
              </div>

              <div className="pt-4 border-t border-border">
                <h4 className="text-xs font-semibold text-text-primary mb-3">Windows Update & Maintenance Status</h4>
                <div className="flex flex-wrap items-center gap-4 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="text-text-muted">Patch Compliance:</span>
                    <Badge variant={device.patchStatus}>{device.patchStatus.replace('_', ' ')}</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-text-muted">Pending Reboot:</span>
                    <Badge variant={device.pendingReboot ? 'warning' : 'online'}>
                      {device.pendingReboot ? 'Reboot Required' : 'Up to date'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-text-muted">Agent Status:</span>
                    <Badge variant="online">Running (v{device.agentVersion})</Badge>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </TabContent>

        {/* NETWORK TAB */}
        <TabContent active={activeTab} tabId="network">
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
              <Card>
                <CardBody className="flex items-center justify-between">
                  <div>
                    <span className="text-text-muted text-2xs uppercase block">Public IPv4</span>
                    <span className="text-base font-bold text-text-primary font-mono">{device.publicIp}</span>
                  </div>
                  <Network className="h-6 w-6 text-accent opacity-80" />
                </CardBody>
              </Card>
              <Card>
                <CardBody className="flex items-center justify-between">
                  <div>
                    <span className="text-text-muted text-2xs uppercase block">Local IPv4</span>
                    <span className="text-base font-bold text-text-primary font-mono">{device.localIp}</span>
                  </div>
                  <Wifi className="h-6 w-6 text-accent opacity-80" />
                </CardBody>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Network className="h-4 w-4 text-accent" />
                  Network Interfaces
                </CardTitle>
              </CardHeader>
              <CardBody className="p-0 overflow-x-auto">
                <table className="w-full text-xs text-left">
                  <thead className="bg-base-panel border-b border-border text-text-muted font-medium uppercase text-2xs">
                    <tr>
                      <th className="px-4 py-3">Interface Name</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">MAC Address</th>
                      <th className="px-4 py-3">IP Address</th>
                      <th className="px-4 py-3">Subnet Mask</th>
                      <th className="px-4 py-3">Gateway</th>
                      <th className="px-4 py-3">DNS Servers</th>
                      <th className="px-4 py-3">Type</th>
                      <th className="px-4 py-3">Link Speed</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {device.networkInterfaces && device.networkInterfaces.length > 0 ? (
                      device.networkInterfaces.map((iface: any, i: number) => (
                        <tr key={i} className="hover:bg-base-hover/50">
                          <td className="px-4 py-3 font-semibold text-text-primary">{iface.name}</td>
                          <td className="px-4 py-3">
                            <Badge variant={iface.status === 'up' ? 'online' : 'offline'}>
                              {(iface.status || 'down').toUpperCase()}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 font-mono text-text-secondary">{iface.mac}</td>
                          <td className="px-4 py-3 font-mono text-text-primary">{iface.ip}</td>
                          <td className="px-4 py-3 font-mono text-text-muted">{iface.subnet}</td>
                          <td className="px-4 py-3 font-mono text-text-muted">{iface.gateway}</td>
                          <td className="px-4 py-3 font-mono text-text-muted">{iface.dns?.join(', ') || '-'}</td>
                          <td className="px-4 py-3 uppercase text-text-secondary">{iface.type}</td>
                          <td className="px-4 py-3 tabular text-text-primary font-medium">{iface.speedMbps} Mbps</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={9} className="px-4 py-6 text-center text-text-muted">
                          No network interfaces reported
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </CardBody>
            </Card>
          </div>
        </TabContent>

        {/* STORAGE TAB */}
        <TabContent active={activeTab} tabId="storage">
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {device.disks && device.disks.length > 0 ? (
                device.disks.map((disk: any) => {
                  const freeGb = Math.max(0, disk.totalGb - disk.usedGb);
                  const usagePct = Math.round((disk.usedGb / disk.totalGb) * 100);
                  return (
                    <Card key={disk.drive}>
                      <CardHeader>
                        <CardTitle className="flex items-center justify-between w-full">
                          <span className="flex items-center gap-2">
                            <HardDrive className="h-4 w-4 text-accent" />
                            Drive {disk.drive} ({disk.label || 'Volume'})
                          </span>
                          <Badge variant="default" className="uppercase">{disk.type}</Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardBody className="space-y-3">
                        <div className="flex items-baseline justify-between">
                          <span className="text-2xl font-bold text-text-primary tabular">{usagePct}%</span>
                          <span className="text-xs text-text-muted">
                            {disk.usedGb} GB used of {disk.totalGb} GB
                          </span>
                        </div>
                        <ProgressBar value={usagePct} showLabel={false} />
                        <div className="pt-2 border-t border-border text-xs space-y-1 text-text-muted">
                          <div className="flex justify-between">
                            <span>File System:</span>
                            <span className="text-text-primary font-medium">NTFS</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Free Space:</span>
                            <span className="text-text-primary font-medium">{freeGb} GB</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Volume Health:</span>
                            <span className="text-status-online font-medium">Healthy</span>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  );
                })
              ) : (
                <EmptyState title="No Storage Drives" description="No storage drives detected on this endpoint." />
              )}
            </div>
          </div>
        </TabContent>

        {/* SOFTWARE TAB */}
        <TabContent active={activeTab} tabId="software">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-4">
              <CardTitle className="flex items-center gap-2">
                <FolderOpen className="h-4 w-4 text-accent" />
                Installed Software
              </CardTitle>
              <div className="relative w-64">
                <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
                <input
                  type="text"
                  placeholder="Search software..."
                  value={softwareSearch}
                  onChange={(e) => setSoftwareSearch(e.target.value)}
                  className="w-full bg-base-elevated border border-border rounded pl-8 pr-3 py-1 text-xs text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent"
                />
              </div>
            </CardHeader>
            <CardBody className="p-0 overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead className="bg-base-panel border-b border-border text-text-muted font-medium uppercase text-2xs">
                  <tr>
                    <th className="px-4 py-3">Software Name</th>
                    <th className="px-4 py-3">Publisher</th>
                    <th className="px-4 py-3">Version</th>
                    <th className="px-4 py-3">Installed Date</th>
                    <th className="px-4 py-3">Size</th>
                    <th className="px-4 py-3">Category</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredSoftware.length > 0 ? (
                    filteredSoftware.map((sw: any) => (
                      <tr key={sw.id || sw.name} className="hover:bg-base-hover/50">
                        <td className="px-4 py-3 font-semibold text-text-primary">{sw.name}</td>
                        <td className="px-4 py-3 text-text-secondary">{sw.publisher}</td>
                        <td className="px-4 py-3 font-mono text-text-muted">{sw.version}</td>
                        <td className="px-4 py-3 text-text-muted">{formatDate(sw.installDate, 'MMM d, yyyy')}</td>
                        <td className="px-4 py-3 tabular text-text-secondary">{sw.sizeMb} MB</td>
                        <td className="px-4 py-3">
                          <Badge variant="default">{sw.category}</Badge>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="px-4 py-6 text-center text-text-muted">
                        No software applications match your search query
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardBody>
          </Card>
        </TabContent>

        {/* SERVICES TAB */}
        <TabContent active={activeTab} tabId="services">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-4">
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-accent" />
                Windows Services
              </CardTitle>
              <div className="relative w-64">
                <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
                <input
                  type="text"
                  placeholder="Filter services..."
                  value={serviceFilter}
                  onChange={(e) => setServiceFilter(e.target.value)}
                  className="w-full bg-base-elevated border border-border rounded pl-8 pr-3 py-1 text-xs text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent"
                />
              </div>
            </CardHeader>
            <CardBody className="p-0 overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead className="bg-base-panel border-b border-border text-text-muted font-medium uppercase text-2xs">
                  <tr>
                    <th className="px-4 py-3">Service Name</th>
                    <th className="px-4 py-3">Display Name</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Start Type</th>
                    <th className="px-4 py-3">PID</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredServices.length > 0 ? (
                    filteredServices.map((svc: any) => (
                      <tr key={svc.name} className="hover:bg-base-hover/50">
                        <td className="px-4 py-3 font-mono font-semibold text-text-primary">{svc.name}</td>
                        <td className="px-4 py-3 text-text-secondary">{svc.displayName}</td>
                        <td className="px-4 py-3">
                          <Badge variant={svc.status}>{(svc.status || 'unknown').toUpperCase()}</Badge>
                        </td>
                        <td className="px-4 py-3 text-text-muted uppercase text-2xs">{svc.startType}</td>
                        <td className="px-4 py-3 font-mono text-text-muted">{svc.pid ?? '-'}</td>
                        <td className="px-4 py-3 text-right space-x-1">
                          {svc.status !== 'running' && (
                            <button
                              onClick={() => handleServiceAction(svc.displayName, 'start')}
                              className="px-2 py-0.5 text-2xs font-medium rounded bg-status-online/10 text-status-online hover:bg-status-online/20 border border-status-online/30 transition-colors"
                            >
                              Start
                            </button>
                          )}
                          {svc.status === 'running' && (
                            <button
                              onClick={() => handleServiceAction(svc.displayName, 'stop')}
                              className="px-2 py-0.5 text-2xs font-medium rounded bg-status-critical/10 text-status-critical hover:bg-status-critical/20 border border-status-critical/30 transition-colors"
                            >
                              Stop
                            </button>
                          )}
                          <button
                            onClick={() => handleServiceAction(svc.displayName, 'restart')}
                            className="px-2 py-0.5 text-2xs font-medium rounded bg-base-elevated text-text-secondary hover:text-text-primary border border-border transition-colors"
                          >
                            Restart
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="px-4 py-6 text-center text-text-muted">
                        No services found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardBody>
          </Card>
        </TabContent>

        {/* PROCESSES TAB */}
        <TabContent active={activeTab} tabId="processes">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-4">
              <CardTitle className="flex items-center gap-2">
                <Sliders className="h-4 w-4 text-accent" />
                Active Processes
              </CardTitle>
              <div className="flex items-center gap-2 text-xs">
                <span className="text-text-muted">Sort by:</span>
                {(['cpu', 'memory', 'name', 'pid'] as const).map((key) => (
                  <button
                    key={key}
                    onClick={() => {
                      if (processSortKey === key) {
                        setProcessSortOrder(processSortOrder === 'asc' ? 'desc' : 'asc');
                      } else {
                        setProcessSortKey(key);
                        setProcessSortOrder('desc');
                      }
                    }}
                    className={cn(
                      'px-2 py-1 rounded text-2xs uppercase font-medium border transition-colors',
                      processSortKey === key
                        ? 'bg-accent text-accent-fg border-accent'
                        : 'bg-base-elevated border-border text-text-muted hover:text-text-primary'
                    )}
                  >
                    {key} {processSortKey === key ? (processSortOrder === 'asc' ? '↑' : '↓') : ''}
                  </button>
                ))}
              </div>
            </CardHeader>
            <CardBody className="p-0 overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead className="bg-base-panel border-b border-border text-text-muted font-medium uppercase text-2xs">
                  <tr>
                    <th className="px-4 py-3">PID</th>
                    <th className="px-4 py-3">Process Name</th>
                    <th className="px-4 py-3">CPU Usage</th>
                    <th className="px-4 py-3">Memory (MB)</th>
                    <th className="px-4 py-3">User</th>
                    <th className="px-4 py-3">Threads</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {sortedProcesses.length > 0 ? (
                    sortedProcesses.map((proc) => (
                      <tr key={proc.pid} className="hover:bg-base-hover/50">
                        <td className="px-4 py-3 font-mono text-text-muted">{proc.pid}</td>
                        <td className="px-4 py-3 font-mono font-semibold text-text-primary">{proc.name}</td>
                        <td className="px-4 py-3 tabular font-medium text-text-primary">{proc.cpu}%</td>
                        <td className="px-4 py-3 tabular text-text-secondary">{proc.memoryMb} MB</td>
                        <td className="px-4 py-3 text-text-muted">{proc.user}</td>
                        <td className="px-4 py-3 text-text-muted">{proc.threads}</td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => toast(`End process request sent for PID ${proc.pid} (${proc.name})`, 'warning')}
                            className="px-2 py-0.5 text-2xs font-medium rounded bg-status-critical/10 text-status-critical hover:bg-status-critical/20 border border-status-critical/30 transition-colors"
                          >
                            End Process
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="px-4 py-6 text-center text-text-muted">
                        No active process data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardBody>
          </Card>
        </TabContent>

        {/* EVENT LOGS TAB */}
        <TabContent active={activeTab} tabId="event_logs">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-4">
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-accent" />
                Windows Event Logs
              </CardTitle>
              <div className="flex items-center gap-1.5 text-xs">
                <span className="text-text-muted mr-1">Level Filter:</span>
                {['all', 'information', 'warning', 'error', 'critical'].map((lvl) => (
                  <button
                    key={lvl}
                    onClick={() => setEventLogLevel(lvl)}
                    className={cn(
                      'px-2.5 py-1 rounded text-2xs uppercase font-medium border transition-colors',
                      eventLogLevel === lvl
                        ? 'bg-accent text-accent-fg border-accent'
                        : 'bg-base-elevated border-border text-text-muted hover:text-text-primary'
                    )}
                  >
                    {lvl}
                  </button>
                ))}
              </div>
            </CardHeader>
            <CardBody className="p-0 overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead className="bg-base-panel border-b border-border text-text-muted font-medium uppercase text-2xs">
                  <tr>
                    <th className="px-4 py-3">Timestamp</th>
                    <th className="px-4 py-3">Level</th>
                    <th className="px-4 py-3">Event ID</th>
                    <th className="px-4 py-3">Source</th>
                    <th className="px-4 py-3">Message</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredEventLogs.length > 0 ? (
                    filteredEventLogs.map((log: any) => (
                      <tr key={log.id} className="hover:bg-base-hover/50">
                        <td className="px-4 py-3 text-text-muted whitespace-nowrap">{formatDate(log.timestamp)}</td>
                        <td className="px-4 py-3">
                          <Badge variant={log.level}>{log.level}</Badge>
                        </td>
                        <td className="px-4 py-3 font-mono font-semibold text-text-primary">{log.eventId}</td>
                        <td className="px-4 py-3 font-medium text-text-secondary">{log.source}</td>
                        <td className="px-4 py-3 text-text-secondary max-w-md truncate">{log.message}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="px-4 py-6 text-center text-text-muted">
                        No event logs found matching selected level filter
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardBody>
          </Card>
        </TabContent>

        {/* PATCHES TAB */}
        <TabContent active={activeTab} tabId="patches">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-4">
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-accent" />
                Patch Management
              </CardTitle>
              <div className="flex items-center gap-1.5 text-xs">
                <span className="text-text-muted mr-1">Status:</span>
                {['all', 'missing', 'installed', 'failed', 'pending_install'].map((st) => (
                  <button
                    key={st}
                    onClick={() => setPatchFilter(st)}
                    className={cn(
                      'px-2.5 py-1 rounded text-2xs uppercase font-medium border transition-colors',
                      patchFilter === st
                        ? 'bg-accent text-accent-fg border-accent'
                        : 'bg-base-elevated border-border text-text-muted hover:text-text-primary'
                    )}
                  >
                    {st.replace('_', ' ')}
                  </button>
                ))}
              </div>
            </CardHeader>
            <CardBody className="p-0 overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead className="bg-base-panel border-b border-border text-text-muted font-medium uppercase text-2xs">
                  <tr>
                    <th className="px-4 py-3">KB Article</th>
                    <th className="px-4 py-3">Patch Title</th>
                    <th className="px-4 py-3">Severity</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Size</th>
                    <th className="px-4 py-3">Installed Date</th>
                    <th className="px-4 py-3">Category</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredPatches.length > 0 ? (
                    filteredPatches.map((patch: any) => (
                      <tr key={patch.kb} className="hover:bg-base-hover/50">
                        <td className="px-4 py-3 font-mono font-semibold text-text-primary">{patch.kb}</td>
                        <td className="px-4 py-3 font-medium text-text-primary max-w-sm truncate">{patch.title}</td>
                        <td className="px-4 py-3">
                          <Badge variant={patch.severity}>{patch.severity}</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={patch.status}>{patch.status.replace('_', ' ')}</Badge>
                        </td>
                        <td className="px-4 py-3 tabular text-text-muted">{patch.sizeMb} MB</td>
                        <td className="px-4 py-3 text-text-muted">
                          {patch.installedDate ? formatDate(patch.installedDate) : '-'}
                        </td>
                        <td className="px-4 py-3 text-text-secondary">{patch.category}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="px-4 py-6 text-center text-text-muted">
                        No patches found matching filter
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardBody>
          </Card>
        </TabContent>

        {/* SECURITY TAB */}
        <TabContent active={activeTab} tabId="security">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-accent" />
                  Antivirus Protection
                </CardTitle>
              </CardHeader>
              <CardBody className="space-y-3 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-text-muted">Status:</span>
                  <Badge variant={device.antivirus}>{(device.antivirus || 'unknown').toUpperCase()}</Badge>
                </div>
                <div className="flex justify-between py-1 border-t border-border">
                  <span className="text-text-muted">Product Engine:</span>
                  <span className="text-text-primary font-medium">Microsoft Defender Antivirus</span>
                </div>
                <div className="flex justify-between py-1 border-t border-border">
                  <span className="text-text-muted">Definitions Version:</span>
                  <span className="text-text-primary font-mono">1.403.2109.0</span>
                </div>
                <div className="flex justify-between py-1 border-t border-border">
                  <span className="text-text-muted">Last Full Scan:</span>
                  <span className="text-text-primary">Yesterday at 22:00</span>
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="h-4 w-4 text-accent" />
                  Firewall & Disk Encryption
                </CardTitle>
              </CardHeader>
              <CardBody className="space-y-3 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-text-muted">Windows Firewall:</span>
                  <Badge variant="online">ACTIVE</Badge>
                </div>
                <div className="flex justify-between py-1 border-t border-border">
                  <span className="text-text-muted">Profiles Protected:</span>
                  <span className="text-text-primary font-medium">Domain, Private, Public</span>
                </div>
                <div className="flex items-center justify-between py-1 border-t border-border">
                  <span className="text-text-muted">BitLocker Encryption:</span>
                  <Badge variant="online">ENABLED (AES-256)</Badge>
                </div>
                <div className="flex justify-between py-1 border-t border-border">
                  <span className="text-text-muted">Encrypted Volumes:</span>
                  <span className="text-text-primary font-medium">C: (System)</span>
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-accent" />
                  Security Posture
                </CardTitle>
              </CardHeader>
              <CardBody className="space-y-3 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-text-muted">Pending Reboot:</span>
                  <Badge variant={device.pendingReboot ? 'warning' : 'online'}>
                    {device.pendingReboot ? 'REBOOT REQUIRED' : 'NO REBOOT PENDING'}
                  </Badge>
                </div>
                <div className="flex items-center justify-between py-1 border-t border-border">
                  <span className="text-text-muted">Real-time Protection:</span>
                  <span className="text-status-online font-semibold">Enabled</span>
                </div>
                <div className="flex items-center justify-between py-1 border-t border-border">
                  <span className="text-text-muted">SmartScreen Filter:</span>
                  <span className="text-status-online font-semibold">Enabled</span>
                </div>
                <div className="flex items-center justify-between py-1 border-t border-border">
                  <span className="text-text-muted">Tamper Protection:</span>
                  <span className="text-status-online font-semibold">Enabled</span>
                </div>
              </CardBody>
            </Card>
          </div>
        </TabContent>

        {/* AUTOMATION TAB */}
        <TabContent active={activeTab} tabId="automation">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-4 w-4 text-accent" />
                  Assigned Monitoring & Alert Rules
                </CardTitle>
              </CardHeader>
              <CardBody className="p-0">
                <div className="divide-y divide-border text-xs">
                  <div className="p-4 flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-text-primary">High CPU Utilization Threshold</div>
                      <div className="text-text-muted text-2xs mt-0.5">Triggers warning alert when CPU exceeds 90% for &gt;5 minutes</div>
                    </div>
                    <Badge variant="online">ACTIVE</Badge>
                  </div>
                  <div className="p-4 flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-text-primary">Low Storage Capacity Monitor</div>
                      <div className="text-text-muted text-2xs mt-0.5">Triggers critical alert when disk space falls below 10%</div>
                    </div>
                    <Badge variant="online">ACTIVE</Badge>
                  </div>
                  <div className="p-4 flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-text-primary">Critical Service Auto-Recovery</div>
                      <div className="text-text-muted text-2xs mt-0.5">Attempts restart for Print Spooler and W3SVC on failure</div>
                    </div>
                    <Badge variant="online">ACTIVE</Badge>
                  </div>
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-accent" />
                  Scheduled Automation Tasks
                </CardTitle>
              </CardHeader>
              <CardBody className="p-0">
                <div className="divide-y divide-border text-xs">
                  <div className="p-4 flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-text-primary">Daily System Inventory Scan</div>
                      <div className="text-text-muted text-2xs mt-0.5">Runs daily at 02:00 AM • Next run: Tomorrow at 02:00 AM</div>
                    </div>
                    <Badge variant="default">SCHEDULED</Badge>
                  </div>
                  <div className="p-4 flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-text-primary">Weekly Patch Compliance Scan</div>
                      <div className="text-text-muted text-2xs mt-0.5">Runs every Sunday at 01:00 AM • Next run: Sunday</div>
                    </div>
                    <Badge variant="default">SCHEDULED</Badge>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        </TabContent>

        {/* SCRIPTS TAB */}
        <TabContent active={activeTab} tabId="scripts">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FileCode2 className="h-4 w-4 text-accent" />
                Script Execution History
              </CardTitle>
              <Button variant="primary" size="sm" onClick={() => setRunScriptModalOpen(true)}>
                <Play className="h-3.5 w-3.5 mr-1" />
                Run Script
              </Button>
            </CardHeader>
            <CardBody className="p-0 overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead className="bg-base-panel border-b border-border text-text-muted font-medium uppercase text-2xs">
                  <tr>
                    <th className="px-4 py-3">Script Name</th>
                    <th className="px-4 py-3">Category</th>
                    <th className="px-4 py-3">Execution Context</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Exit Code</th>
                    <th className="px-4 py-3">Executed At</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  <tr className="hover:bg-base-hover/50">
                    <td className="px-4 py-3 font-semibold text-text-primary">Clear Windows Temp Files</td>
                    <td className="px-4 py-3 text-text-secondary">Maintenance</td>
                    <td className="px-4 py-3 font-mono text-text-muted">SYSTEM</td>
                    <td className="px-4 py-3"><Badge variant="online">COMPLETED</Badge></td>
                    <td className="px-4 py-3 font-mono text-text-primary">0</td>
                    <td className="px-4 py-3 text-text-muted">2 hours ago</td>
                  </tr>
                  <tr className="hover:bg-base-hover/50">
                    <td className="px-4 py-3 font-semibold text-text-primary">Flush DNS & Reset Winsock</td>
                    <td className="px-4 py-3 text-text-secondary">Network</td>
                    <td className="px-4 py-3 font-mono text-text-muted">SYSTEM</td>
                    <td className="px-4 py-3"><Badge variant="online">COMPLETED</Badge></td>
                    <td className="px-4 py-3 font-mono text-text-primary">0</td>
                    <td className="px-4 py-3 text-text-muted">Yesterday at 14:30</td>
                  </tr>
                  <tr className="hover:bg-base-hover/50">
                    <td className="px-4 py-3 font-semibold text-text-primary">Verify Disk Fragmentation</td>
                    <td className="px-4 py-3 text-text-secondary">Diagnostics</td>
                    <td className="px-4 py-3 font-mono text-text-muted">SYSTEM</td>
                    <td className="px-4 py-3"><Badge variant="online">COMPLETED</Badge></td>
                    <td className="px-4 py-3 font-mono text-text-primary">0</td>
                    <td className="px-4 py-3 text-text-muted">3 days ago</td>
                  </tr>
                </tbody>
              </table>
            </CardBody>
          </Card>
        </TabContent>

        {/* ACTIVITY TAB */}
        <TabContent active={activeTab} tabId="activity">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-accent" />
                Device Event Timeline
              </CardTitle>
            </CardHeader>
            <CardBody className="p-4">
              <div className="relative pl-6 space-y-6 before:absolute before:left-2 before:top-2 before:bottom-2 before:w-0.5 before:bg-border">
                <div className="relative">
                  <div className="absolute -left-6 top-0.5 h-4 w-4 rounded-full bg-base-elevated border border-accent flex items-center justify-center text-accent">
                    <Clock className="h-2.5 w-2.5" />
                  </div>
                  <div className="text-xs">
                    <span className="font-semibold text-text-primary">Agent Heartbeat Confirmed</span>
                    <span className="text-text-muted ml-2">{timeAgo(device.lastSeen)}</span>
                    <p className="text-text-muted text-2xs mt-0.5">Regular status check-in received from agent v{device.agentVersion}</p>
                  </div>
                </div>

                <div className="relative">
                  <div className="absolute -left-6 top-0.5 h-4 w-4 rounded-full bg-base-elevated border border-border text-status-online flex items-center justify-center">
                    <CheckCircle className="h-2.5 w-2.5" />
                  </div>
                  <div className="text-xs">
                    <span className="font-semibold text-text-primary">Script Execution Completed</span>
                    <span className="text-text-muted ml-2">2 hours ago</span>
                    <p className="text-text-muted text-2xs mt-0.5">Automated script "Clear Windows Temp Files" executed with exit code 0</p>
                  </div>
                </div>

                <div className="relative">
                  <div className="absolute -left-6 top-0.5 h-4 w-4 rounded-full bg-base-elevated border border-border text-accent flex items-center justify-center">
                    <RotateCw className="h-2.5 w-2.5" />
                  </div>
                  <div className="text-xs">
                    <span className="font-semibold text-text-primary">System Boot</span>
                    <span className="text-text-muted ml-2">{formatDate(device.lastBoot)}</span>
                    <p className="text-text-muted text-2xs mt-0.5">Operating system reported normal system startup</p>
                  </div>
                </div>

                <div className="relative">
                  <div className="absolute -left-6 top-0.5 h-4 w-4 rounded-full bg-base-elevated border border-border text-text-secondary flex items-center justify-center">
                    <Server className="h-2.5 w-2.5" />
                  </div>
                  <div className="text-xs">
                    <span className="font-semibold text-text-primary">Endpoint Enrolled</span>
                    <span className="text-text-muted ml-2">{formatDate(device.enrolledAt)}</span>
                    <p className="text-text-muted text-2xs mt-0.5">Device initially registered to client {device.client} site {device.site}</p>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </TabContent>

        {/* REMOTE TOOLS TAB */}
        <TabContent active={activeTab} tabId="remote_tools">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wrench className="h-4 w-4 text-accent" />
                Remote Management Console
              </CardTitle>
            </CardHeader>
            <CardBody>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                <button
                  onClick={() => navigate(`/devices/${device.id}/remote-desktop`)}
                  className="surface p-4 flex flex-col items-center justify-center text-center gap-2 hover:border-accent transition-colors group rounded border border-border"
                >
                  <Monitor className="h-6 w-6 text-accent group-hover:scale-110 transition-transform" />
                  <span className="text-xs font-semibold text-text-primary">Remote Desktop</span>
                  <span className="text-2xs text-text-muted">Launch interactive GUI desktop session</span>
                </button>

                <button
                  onClick={() => navigate(`/devices/${device.id}/files`)}
                  className="surface p-4 flex flex-col items-center justify-center text-center gap-2 hover:border-accent transition-colors group rounded border border-border"
                >
                  <FolderOpen className="h-6 w-6 text-accent group-hover:scale-110 transition-transform" />
                  <span className="text-xs font-semibold text-text-primary">File Explorer</span>
                  <span className="text-2xs text-text-muted">Browse, download, upload system files</span>
                </button>

                <button
                  onClick={() => navigate(`/devices/${device.id}/powershell`)}
                  className="surface p-4 flex flex-col items-center justify-center text-center gap-2 hover:border-accent transition-colors group rounded border border-border"
                >
                  <Terminal className="h-6 w-6 text-accent group-hover:scale-110 transition-transform" />
                  <span className="text-xs font-semibold text-text-primary">PowerShell Terminal</span>
                  <span className="text-2xs text-text-muted">Open remote admin PowerShell session</span>
                </button>

                <button
                  onClick={() => navigate(`/devices/${device.id}/cmd`)}
                  className="surface p-4 flex flex-col items-center justify-center text-center gap-2 hover:border-accent transition-colors group rounded border border-border"
                >
                  <TerminalSquare className="h-6 w-6 text-accent group-hover:scale-110 transition-transform" />
                  <span className="text-xs font-semibold text-text-primary">CMD Prompt</span>
                  <span className="text-2xs text-text-muted">Execute remote Windows command line</span>
                </button>

                <button
                  onClick={() => handleAction('lock')}
                  className="surface p-4 flex flex-col items-center justify-center text-center gap-2 hover:border-accent transition-colors group rounded border border-border"
                >
                  <Lock className="h-6 w-6 text-text-secondary group-hover:scale-110 transition-transform" />
                  <span className="text-xs font-semibold text-text-primary">Lock Workstation</span>
                  <span className="text-2xs text-text-muted">Lock active user desktop screen</span>
                </button>

                <button
                  onClick={() => handleAction('sign_out')}
                  className="surface p-4 flex flex-col items-center justify-center text-center gap-2 hover:border-accent transition-colors group rounded border border-border"
                >
                  <LogOut className="h-6 w-6 text-text-secondary group-hover:scale-110 transition-transform" />
                  <span className="text-xs font-semibold text-text-primary">Sign Out User</span>
                  <span className="text-2xs text-text-muted">Log off current user account</span>
                </button>

                <button
                  onClick={() => setConfirmAction('restart')}
                  className="surface p-4 flex flex-col items-center justify-center text-center gap-2 hover:border-status-warning transition-colors group rounded border border-border"
                >
                  <RotateCw className="h-6 w-6 text-status-warning group-hover:scale-110 transition-transform" />
                  <span className="text-xs font-semibold text-text-primary">Restart Endpoint</span>
                  <span className="text-2xs text-text-muted">Reboot system immediately</span>
                </button>

                <button
                  onClick={() => setConfirmAction('shutdown')}
                  className="surface p-4 flex flex-col items-center justify-center text-center gap-2 hover:border-status-critical transition-colors group rounded border border-border"
                >
                  <Power className="h-6 w-6 text-status-critical group-hover:scale-110 transition-transform" />
                  <span className="text-xs font-semibold text-text-primary">Power Off</span>
                  <span className="text-2xs text-text-muted">Shut down machine power</span>
                </button>
              </div>
            </CardBody>
          </Card>
        </TabContent>

        {/* SETTINGS TAB */}
        <TabContent active={activeTab} tabId="settings">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-4 w-4 text-accent" />
                Device Configuration & Agent Settings
              </CardTitle>
            </CardHeader>
            <CardBody className="space-y-6 text-xs max-w-xl">
              <div className="space-y-1.5">
                <label className="text-text-secondary font-medium block">Hostname / Alias</label>
                <input
                  type="text"
                  defaultValue={device.hostname}
                  className="w-full bg-base-elevated border border-border rounded px-3 py-1.5 text-xs text-text-primary focus:outline-none focus:border-accent"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-text-secondary font-medium block">Agent Heartbeat Interval</label>
                <select
                  value={heartbeatInterval}
                  onChange={(e) => setHeartbeatInterval(e.target.value)}
                  className="w-full bg-base-elevated border border-border rounded px-3 py-1.5 text-xs text-text-primary focus:outline-none focus:border-accent"
                >
                  <option value="15">15 seconds (High Frequency)</option>
                  <option value="30">30 seconds</option>
                  <option value="60">60 seconds (Standard)</option>
                  <option value="300">5 minutes (Low Overhead)</option>
                </select>
              </div>

              {/* Tag Editor */}
              <div className="space-y-2">
                <label className="text-text-secondary font-medium block">Device Tags</label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {deviceTags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 bg-base-elevated border border-border text-text-secondary rounded text-2xs px-2 py-0.5"
                    >
                      <Tag className="h-2.5 w-2.5 text-accent" />
                      {tag}
                      <button onClick={() => handleRemoveTag(tag)} className="hover:text-status-critical">
                        <X className="h-2.5 w-2.5" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Add new tag..."
                    value={newTagInput}
                    onChange={(e) => setNewTagInput(e.target.value)}
                    className="flex-1 bg-base-elevated border border-border rounded px-3 py-1 text-xs text-text-primary focus:outline-none focus:border-accent"
                  />
                  <Button variant="secondary" size="sm" onClick={handleAddTag}>
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    Add Tag
                  </Button>
                </div>
              </div>

              <div className="pt-2 border-t border-border flex items-center justify-between">
                <div>
                  <span className="font-semibold text-text-primary block">Maintenance Mode</span>
                  <span className="text-text-muted text-2xs">Suppress automated alerts during maintenance windows</span>
                </div>
                <button
                  type="button"
                  onClick={() => setMaintenanceMode(!maintenanceMode)}
                  className={cn(
                    'w-10 h-5 rounded-full p-0.5 transition-colors relative',
                    maintenanceMode ? 'bg-accent' : 'bg-base-elevated border border-border'
                  )}
                >
                  <div
                    className={cn(
                      'w-4 h-4 rounded-full bg-white transition-transform',
                      maintenanceMode ? 'translate-x-5' : 'translate-x-0'
                    )}
                  />
                </button>
              </div>

              <div className="pt-4 border-t border-border">
                <Button variant="primary" onClick={handleSaveSettings}>
                  Save Changes
                </Button>
              </div>
            </CardBody>
          </Card>
        </TabContent>

      </div>

      {/* Destructive Action Confirm Modal */}
      <ConfirmDialog
        open={confirmAction !== null}
        onClose={() => setConfirmAction(null)}
        onConfirm={() => {
          if (confirmAction === 'restart') {
            toast(`Restart command issued for ${device.hostname}`);
            setConfirmAction(null);
          } else if (confirmAction === 'shutdown') {
            toast(`Shutdown command issued for ${device.hostname}`, 'warning');
            setConfirmAction(null);
          } else if (confirmAction === 'delete') {
            void apiService.deleteDevice(device.id).then(() => {
              toast(`${device.hostname} deleted. It will reappear if that PC comes back online.`);
              setConfirmAction(null);
              navigate('/devices');
            }).catch((err: any) => {
              toast(err?.message || 'Delete failed');
              setConfirmAction(null);
            });
          }
        }}
        title={
          confirmAction === 'restart'
            ? 'Restart Device'
            : confirmAction === 'delete'
            ? 'Delete Offline Device'
            : 'Shut Down Device'
        }
        message={
          confirmAction === 'restart'
            ? `Are you sure you want to restart ${device.hostname}? Active user sessions will be disconnected.`
            : confirmAction === 'delete'
            ? `This hides ${device.hostname} from the portal. If that PC is switched on and the agent is still installed, it will come back on its own.`
            : `Are you sure you want to shut down ${device.hostname}? The endpoint will need to be manually powered back on.`
        }
        confirmLabel={
          confirmAction === 'restart' ? 'Restart' : confirmAction === 'delete' ? 'Delete' : 'Shut Down'
        }
        danger={confirmAction === 'shutdown' || confirmAction === 'delete'}
      />

      {/* Run Script Modal */}
      <Modal
        open={runScriptModalOpen}
        onClose={() => setRunScriptModalOpen(false)}
        title="Run Script on Endpoint"
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setRunScriptModalOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                toast(`Execution of "${selectedScript}" initiated on ${device.hostname}`);
                setRunScriptModalOpen(false);
              }}
            >
              Execute Now
            </Button>
          </>
        }
      >
        <div className="space-y-4 text-xs">
          <div>
            <label className="text-text-secondary font-medium block mb-1">Select Script</label>
            <select
              value={selectedScript}
              onChange={(e) => setSelectedScript(e.target.value)}
              className="w-full bg-base-elevated border border-border rounded px-3 py-1.5 text-xs text-text-primary focus:outline-none focus:border-accent"
            >
              <option value="System Health Diagnostics">System Health Diagnostics (PowerShell)</option>
              <option value="Clear Windows Temp Files">Clear Windows Temp Files (PowerShell)</option>
              <option value="Flush DNS & Network Reset">Flush DNS & Network Reset (CMD)</option>
              <option value="Restart Print Spooler">Restart Print Spooler (PowerShell)</option>
            </select>
          </div>
          <div>
            <label className="text-text-secondary font-medium block mb-1">Target Endpoint</label>
            <input
              type="text"
              readOnly
              value={`${device.hostname} (${device.localIp})`}
              className="w-full bg-base-panel border border-border rounded px-3 py-1.5 text-xs text-text-muted cursor-not-allowed"
            />
          </div>
          <div>
            <label className="text-text-secondary font-medium block mb-1">Execution Context</label>
            <div className="flex gap-4 text-text-secondary">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="radio" name="execContext" defaultChecked className="accent-accent" />
                NT AUTHORITY\SYSTEM
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="radio" name="execContext" className="accent-accent" />
                Logged In User
              </label>
            </div>
          </div>
        </div>
      </Modal>
    </PageContainer>
  );
}
